// AJAX pagination without a full page reload: fetches the next page via Shopify's `?section_id=`
// endpoint and either appends or replaces content.
//
//   <ajax-pagination id="some-id" content-selector=".my-list" mode="append|replace" data-remember trigger="scroll">
//     <a href="...?page=2">...</a>       (real link — still works without JS)
//   </ajax-pagination>
//
// Params:
//   content-selector — CSS selector for the product list block to update
//   mode             — "append" (load more) or "replace" (numbered pages)
//   data-remember    — remembers the reached page (localStorage, 1 day TTL), restores it on back-navigation
//   trigger="scroll" — auto-loads the next page on approach (infinite scroll) instead of waiting for a
//                      click; only meaningful with mode="append"
import { cachedFetch, extractSectionId } from 'theme';

const STORAGE_PREFIX = 'theme:ajax-pagination:';
const REMEMBER_TTL = 24 * 60 * 60 * 1000; // 1 day

const storageKey = (id) => `${STORAGE_PREFIX}${window.location.pathname}:${id}`;

const saveProgress = (id, page) => {
  try {
    localStorage.setItem(storageKey(id), JSON.stringify({ page, savedAt: Date.now() }));
  } catch {
    // Storage unavailable (private browsing, quota, etc.) — nothing to remember, nothing breaks.
  }
};

const readSavedPage = (id) => {
  try {
    const raw = localStorage.getItem(storageKey(id));
    if (!raw) return null;

    const { page, savedAt } = JSON.parse(raw);
    if (!page || !savedAt || Date.now() - savedAt > REMEMBER_TTL) {
      localStorage.removeItem(storageKey(id));
      return null;
    }

    return page;
  } catch {
    return null;
  }
};

const urlForPage = (page) => {
  const url = new URL(window.location.href);
  url.searchParams.set('page', page);
  return url.toString();
};

const fetchSection = async (href, sectionId) => {
  const url = new URL(href, window.location.href);
  url.searchParams.set('section_id', sectionId);
  const html = await cachedFetch(url.toString()).then((response) => response.text());
  return new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
};

// Wires up one <ajax-pagination> element and returns a cleanup function.
function connectAjaxPagination(el) {
  let currentPage = 1;

  const selector = el.getAttribute('content-selector');
  const isReplace = el.getAttribute('mode') === 'replace';
  const remember = el.hasAttribute('data-remember');
  const autoScroll = el.getAttribute('trigger') === 'scroll';
  const content = selector ? el.closest('.shopify-section')?.querySelector(selector) : null;

  const goToPage = async (href, { scroll, persist }) => {
    if (el.hasAttribute('aria-busy')) return;
    el.setAttribute('aria-busy', 'true');

    try {
      const targetPage = parseInt(new URL(href, window.location.href).searchParams.get('page'), 10) || 1;
      const newSection = await fetchSection(href, extractSectionId(el.closest('.shopify-section')));
      if (!newSection) return;

      const newContent = selector ? newSection.querySelector(selector) : null;

      if (content && newContent) {
        if (isReplace) {
          content.replaceChildren(...newContent.childNodes);
        } else {
          content.append(...newContent.childNodes);
        }
      }

      const newControls = el.id ? newSection.querySelector(`#${CSS.escape(el.id)}`) : null;
      el.replaceChildren(...(newControls ? newControls.childNodes : []));

      currentPage = targetPage;

      if (persist && remember) {
        saveProgress(el.id, currentPage);
      }

      if (isReplace && scroll && content) {
        content.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch {
      // Leave the existing controls in place so the visitor can retry.
    } finally {
      el.removeAttribute('aria-busy');
    }
  };

  const restoreProgress = async () => {
    const savedPage = readSavedPage(el.id);
    if (!savedPage || savedPage <= 1) return;

    if (isReplace) {
      await goToPage(urlForPage(savedPage), { scroll: false, persist: false });
      return;
    }

    for (let page = 2; page <= savedPage; page++) {
      await goToPage(urlForPage(page), { scroll: false, persist: false });
      // The fetched controls come back empty once there is no next page — stop replaying rather than
      // re-fetching (and duplicating) the same last page for the remaining planned pages.
      if (el.childElementCount === 0) break;
    }
  };

  const onControlClick = (event) => {
    const link = event.target.closest('a[href]');
    if (!link || !el.contains(link)) return;

    event.preventDefault();
    goToPage(link.href, { scroll: true, persist: true });
  };

  el.addEventListener('click', onControlClick);

  let observer;

  if (autoScroll) {
    // `el` itself commonly has `display: contents` (no box of its own), so it never reports
    // intersections — observe the actual link instead, and re-observe its replacement after each load.
    const observeLink = () => {
      const link = el.querySelector('a[href]');
      if (link) observer.observe(link);
    };

    observer = new IntersectionObserver(async (entries) => {
      const entry = entries.find((e) => e.isIntersecting);
      if (!entry) return;

      observer.unobserve(entry.target);
      await goToPage(entry.target.href, { scroll: false, persist: true });
      observeLink();
    }, { rootMargin: '0px 0px 600px 0px' });

    observeLink();
  }

  if (remember) {
    restoreProgress();
  }

  return () => {
    el.removeEventListener('click', onControlClick);
    observer?.disconnect();
  };
}

if (!window.customElements.get('ajax-pagination')) {
  window.customElements.define(
    'ajax-pagination',
    class extends HTMLElement {
      connectedCallback() {
        this._disconnect = connectAjaxPagination(this);
      }

      disconnectedCallback() {
        this._disconnect?.();
      }
    }
  );
}
